import React, { useEffect, useState } from 'react';
import { useDeliveryDayEdit } from './apiHooks';
import Loader from '../../components/loader';
import ErrorMessage from '../../components/errorMessage';
import utils from '../../utils/utils';
import { FormattedMessage } from 'react-intl';
import AdminRoutePlanV2RouteSetup from './adminRoutePlanV2RouteSetup';
import { faCog, faCopy, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Modal from 'react-bootstrap/Modal';
import AdminRoutePlanV2SetupsDeliveryDaysModal from './adminRoutePlanV2SetupsDeliveryDaysModal';

const AdminRoutePlanV2Setups = ({ deliveryDay, routePlanSetups }) => {
  const [showPreviousDeliveryDayModal, setShowPreviousDeliveryDayModal] = useState(false);
  const [newRoutePlanSetups, setNewRoutePlanSetups] = useState(null);

  const {
    editDeliveryDay,
    isLoading: editLoading,
    isError: isEditError,
    error: editError,
  } = useDeliveryDayEdit((deliveryDay) => {});

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [editError]);

  useEffect(() => {
    setNewRoutePlanSetups(routePlanSetups ? routePlanSetups.map((rps) => utils.getRoutePlanSetupForForm(rps)) : []);
  }, [routePlanSetups]);

  const onDeliveryDayTimeRangeSelect = (index, newValues) => {
    const newSetups = [...newRoutePlanSetups];
    newSetups[index].deliveryDayTimeRanges = newValues || [];
    setNewRoutePlanSetups(newSetups);
  };

  const onRemove = (index) => {
    const newSetups = [...newRoutePlanSetups];
    newSetups.splice(index, 1);
    setNewRoutePlanSetups(newSetups);
  };

  const onPickupSlotRemove = (index, pickupSlotIndex) => {
    const newSetups = [...newRoutePlanSetups];
    newSetups[index].pickupSlots.splice(pickupSlotIndex, 1);
    setNewRoutePlanSetups(newSetups);
  };

  const onPickupSlotAdd = (index) => {
    const newSetups = [...newRoutePlanSetups];
    const deliveryDate = utils.getDateFromString(deliveryDay.deliveryDate);
    newSetups[index].pickupSlots.push({
      pickupFromHour: deliveryDate.format('H'),
      pickupFromMinutes: deliveryDate.format('m'),
      pickupToHour: deliveryDate.format('H'),
      pickupToMinutes: deliveryDate.format('m'),
      startDepotId: null,
      vehicles: [
        {
          vehicleId: 'CAR',
          count: 3,
          capacity: 2,
        },
      ],
    });
    setNewRoutePlanSetups(newSetups);
  };

  const onPickupSlotChange = (index, pickupSlotIndex, newPickupSlot) => {
    const newSetups = [...newRoutePlanSetups];
    newSetups[index].pickupSlots[pickupSlotIndex] = newPickupSlot;
    setNewRoutePlanSetups(newSetups);
  };

  return (
    <>
      <Loader show={editLoading} />
      {isEditError ? <ErrorMessage error={utils.getErrorMessageFromResponse(editError)} /> : ''}

      <div className="m-b-6">
        {newRoutePlanSetups?.map((rps, index) => {
          const availableTimeRanges = deliveryDay.timeRanges.filter(
            (tr) =>
              rps.deliveryDayTimeRanges.some((ddtr) => ddtr == tr.id) ||
              !newRoutePlanSetups.some((nrps) => nrps.deliveryDayTimeRanges.some((ddtr) => ddtr === tr.id))
          );

          return (
            <AdminRoutePlanV2RouteSetup
              key={index}
              routePlanSetup={rps}
              deliveryDate={deliveryDay.deliveryDate}
              deliveryDayTimeRanges={availableTimeRanges}
              onRemove={() => onRemove(index)}
              onDeliveryDayTimeRangeSelect={(newValue) => onDeliveryDayTimeRangeSelect(index, newValue)}
              onPickupSlotAdd={() => onPickupSlotAdd(index)}
              onPickupSlotRemove={(pickupSlotIndex) => onPickupSlotRemove(index, pickupSlotIndex)}
              onPickupSlotChange={(pickupSlotIndex, newPickupSlot) =>
                onPickupSlotChange(index, pickupSlotIndex, newPickupSlot)
              }
            />
          );
        })}
      </div>

      <div className="m-b-6">
        <button
          onClick={() => {
            setShowPreviousDeliveryDayModal(true);
          }}
          className="btn btn-normal-link btn-small m-r-2"
        >
          <FontAwesomeIcon icon={faCopy} />{' '}
          <FormattedMessage id={'admin.route.plan.setups.add.new.from.delivery.day'} />
        </button>
        <button
          onClick={() => {
            setNewRoutePlanSetups([...newRoutePlanSetups, { deliveryDayTimeRanges: [], pickupSlots: [] }]);
          }}
          className="btn btn-normal-link btn-small m-r-2"
        >
          <FontAwesomeIcon icon={faCog} /> <FormattedMessage id={'admin.route.plan.setups.add.new'} />
        </button>
      </div>
      <div className="m-b-6 text-center">
        <button
          onClick={() =>
            editDeliveryDay({
              deliveryDay: { ...utils.getDeliveryDayForForm(deliveryDay), routePlanSetups: newRoutePlanSetups },
              populateRoutePlanSetups: true,
            })
          }
          className={`btn btn-primary ${editLoading ? 'btn-loading' : ''}`}
        >
          <FormattedMessage id={'admin.route.plan.setups.save'} />
        </button>
      </div>

      <Modal show={!!showPreviousDeliveryDayModal} onHide={() => setShowPreviousDeliveryDayModal(false)} size="lg">
        <div onClick={() => setShowPreviousDeliveryDayModal(false)} className="b-modal-close">
          <FontAwesomeIcon icon={faTimes} />
        </div>

        <div className="b-modal">
          {showPreviousDeliveryDayModal ? (
            <AdminRoutePlanV2SetupsDeliveryDaysModal
              onSuccess={(routePlanSetups) => {
                const newRoutePlanSetups = routePlanSetups.map((rps) => {
                  const matchingDeliveryDayTimeRanges = rps.deliveryDayTimeRanges
                    .map((ddtr) =>
                      deliveryDay.timeRanges.find((ddtr2) => {
                        return ddtr2.fromHour === ddtr.fromHour && ddtr2.serviceAreaId === ddtr.serviceAreaId;
                      })
                    )
                    .filter((ddtr) => ddtr)
                    .map((ddtr) => ddtr.id);

                  const transformedRps = utils.getRoutePlanSetupForForm(rps);

                  return {
                    deliveryDayTimeRanges: matchingDeliveryDayTimeRanges,
                    pickupSlots: transformedRps.pickupSlots.map(
                      ({
                        name,
                        pickupFromHour,
                        pickupFromMinutes,
                        pickupToHour,
                        pickupToMinutes,
                        startDepotId,
                        vehicles,
                      }) => {
                        return {
                          name,
                          pickupFromHour,
                          pickupFromMinutes,
                          pickupToHour,
                          pickupToMinutes,
                          startDepotId,
                          vehicles: vehicles.map(
                            ({
                              count,
                              capacity,
                              vehicleId,
                              maxTravelTimeInMinutes,
                              maxLateTimeInMinutes,
                              routingProfileName,
                            }) => ({
                              count,
                              capacity,
                              vehicleId,
                              maxTravelTimeInMinutes,
                              maxLateTimeInMinutes,
                              routingProfileName,
                            })
                          ),
                        };
                      }
                    ),
                  };
                });
                setNewRoutePlanSetups(newRoutePlanSetups);
                setShowPreviousDeliveryDayModal(false);
              }}
            />
          ) : (
            ''
          )}
        </div>
      </Modal>
    </>
  );
};

export default AdminRoutePlanV2Setups;
